import type { ConceptDescription } from '../../Metamodel/ConceptDescription';
import type { Reference } from '../../Metamodel/SupportTypes/Reference';
import type { PagedResult } from '../Interfaces/PagedResult';
import type { PagingParams } from '../Interfaces/PagingParams';
import { assertValidAasResponse, assignPagingParams, assignReferences, assignSearchParams } from '../../Utils/Restutils';
import { ClientBase } from './ClientBase';

export class ConceptDescriptionClient extends ClientBase {
  public async getConceptDescriptions(
    idShort?: string,
    isCaseOf?: Reference,
    dataSpecificationRef?: Reference,
    pagingParams?: PagingParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<ConceptDescription>> {
    const params = new URLSearchParams();
    assignSearchParams(params, {
      idShort,
    });
    assignReferences(params, {
      isCaseOf,
      dataSpecificationRef,
    });
    assignPagingParams(params, pagingParams);
    const [request, response] = await this.request(
      'get',
      '/concept-descriptions',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createConceptDescription(
    conceptDescription: ConceptDescription,
    signal?: AbortSignal,
  ): Promise<ConceptDescription> {
    const [request, response] = await this.request(
      'post',
      '/concept-descriptions',
      undefined,
      JSON.stringify(conceptDescription),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getConceptDescription(
    id: string,
    signal?: AbortSignal,
  ): Promise<ConceptDescription> {
    const [request, response] = await this.request(
      'get',
      `/concept-descriptions/${id}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async replaceConceptDescription(
    id: string,
    conceptDescription: ConceptDescription,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'put',
      `/concept-descriptions/${id}`,
      undefined,
      JSON.stringify(conceptDescription),
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }

  public async deleteConceptDescription(
    id: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/concept-descriptions/${id}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'void');
  }
}
